import { apiBaseUrl } from "@/lib/api/client";
import { shouldSendBearer } from "@/lib/api/authed-fetch";
import { useAuthV2 } from "@/lib/authv2/store";
import { refreshOn401 } from "@/lib/authv2/refreshInterceptor";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

// Khuôn lấy thẳng từ hợp đồng (openapi.yaml → schema.ts): `ledger` chỉ có khi
// máy chủ đọc được bản sổ AFF gửi qua Center (QĐ 0268).
export type MyAffiliateCode = components["schemas"]["AffiliateMyCode"];
export type MyAffiliateLedger = components["schemas"]["AffiliateStaffLedger"];

async function readError(response: Response, fallback = "Không đọc được mã AFF"): Promise<Error> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON: serverErrorMessage falls back to the sentence below.
  }
  return new Error(serverErrorMessage(body, fallback));
}

export async function getMyAffiliateCode(): Promise<MyAffiliateCode> {
  const send = async () => {
    const headers: Record<string, string> = {};
    const { accessToken } = useAuthV2.getState();
    if (accessToken && shouldSendBearer()) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(`${apiBaseUrl}/affiliate/me/code`, {
      method: "GET",
      headers,
      credentials: "include",
    });
  };
  const response = await refreshOn401(await send(), send);
  if (!response.ok) throw await readError(response);
  return (await response.json()) as MyAffiliateCode;
}

export type MyAffiliateLinkRequest = components["schemas"]["AffiliateMyLinkRequest"];

/**
 * Xin link cho một món trong bảng giá (bản 2, CEO 3a 30/10/2026). Máy chủ xếp
 * lệnh sang AFF và trả ngay; link về bằng sổ AFF gửi lại — đọc lại
 * getMyAffiliateCode() sau vài giây. Lỗi mang lý do của máy chủ (mã chưa hoạt
 * động, đủ 300 link, gói chỉ bán tại quầy…).
 */
export async function requestMyAffiliateLink(body: MyAffiliateLinkRequest): Promise<void> {
  const send = async () => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const { accessToken } = useAuthV2.getState();
    if (accessToken && shouldSendBearer()) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(`${apiBaseUrl}/affiliate/me/links`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
    });
  };
  const response = await refreshOn401(await send(), send);
  if (!response.ok) throw await readError(response, "Chưa xin được link cho sản phẩm này");
}
